/* Diversity product page (Oct 2026): interactions for the ds-product* sections.
   Ported from the approved prototype v71 (06_WEBSITE_CRO/mockups/pdp/2026-10-08_pdp-prototype.src.html).
   One custom element per section, so each works on its own and re-initialises when edited in the theme editor:
   - <ds-product>  options, stock, product form, gallery. Radios live inside #variant-selects-{section}; a change
                   updates the theme's product form (hidden name="id"), the main button, the price and ?variant=,
                   then publishes PUB_SUB_EVENTS.variantChange so the existing sticky add-to-cart bar stays in sync.
                   Stock = Shopify availability minus this shopper's cart (/cart.js), refreshed after each add.
   - <ds-why>      « Chacun·e a sa raison » + the quote on black.
   - <ds-story>    blurred photo that turns sharp.
   - <ds-quality>  one quality detail at a time.
   - <ds-reviews>  Judge.me reviews of every product, loaded near the section, shown exactly as written.
   No library. Animations use opacity / transform; scroll handlers are rAF-throttled. */
(() => {
  if (customElements.get('ds-product')) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const clamp = (v) => Math.max(0, Math.min(1, v));
  const MONTHS = {
    fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juill.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
    en: ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'],
  };
  const starHTML = (avg) =>
    `<span class="dstars" aria-hidden="true"><span class="base">★★★★★</span><span class="fill" style="width:${Math.max(0, Math.min(100, (avg / 5) * 100))}%">★★★★★</span></span>`;
  const isEN = (t) => /\b(the|this|is|and|so|my|it|very|love|hood)\b/i.test(t) && !/[éèàçùê]/.test(t);
  const header = () => document.querySelector('.section-header');
  const stickyBar = () => document.querySelector('sticky-add-to-cart');

  /* ---------- shared: modals (size guide, photo zoom, all reviews) and « back to the buy block » links ---------- */
  function openModal(m, opener) {
    m._opener = opener;
    m.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    void m.offsetWidth; // apply the hidden → shown change before the fade starts
    m.classList.add('show');
    const x = m.querySelector('[data-close]');
    if (x) x.focus({ preventScroll: true });
  }
  function closeModal(m) {
    m.classList.remove('show');
    document.documentElement.style.overflow = '';
    setTimeout(() => { if (!m.classList.contains('show')) m.hidden = true; }, 330);
    if (m._opener && m._opener.isConnected) m._opener.focus({ preventScroll: true });
  }
  document.addEventListener('click', (e) => {
    const o = e.target.closest('[data-ds-open]');
    if (o) {
      const m = document.getElementById(o.dataset.dsOpen);
      if (m) openModal(m, o);
      return;
    }
    const m = e.target.closest('.ds-pdp .dmodal');
    if (m && (e.target === m || e.target.closest('[data-close]'))) { closeModal(m); return; }
    const tb = e.target.closest('[data-tobuy]');
    if (tb) {
      e.preventDefault();
      const buy = document.getElementById('ds-buy');
      if (buy) buy.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelectorAll('.ds-pdp .dmodal.show').forEach(closeModal);
  });

  /* ---------- shared: Apple-style word reveal ---------- */
  function splitWords(root) {
    const targets = root.matches('h2,h3,blockquote,b') ? [root] : [...root.querySelectorAll('h2, h3, blockquote, b')];
    targets.forEach((el) => {
      if (el.dataset.split || el.closest('.dstars')) return;
      el.dataset.split = '1';
      el.setAttribute('aria-label', el.textContent.replace(/\s+/g, ' ').trim());
      let i = 0;
      const walk = (node) => {
        [...node.childNodes].forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/([ \t\n\r]+)/).forEach((part) => {
              if (!part) return;
              if (/^[ \t\n\r]+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              const s = document.createElement('span');
              s.className = 'w';
              s.setAttribute('aria-hidden', 'true');
              s.style.setProperty('--i', i++);
              s.textContent = part;
              frag.appendChild(s);
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1 && !n.classList.contains('w') && !n.classList.contains('sub')) walk(n);
        });
      };
      walk(el);
    });
  }

  /* base: listeners and observers are removed when a section is removed or re-rendered in the theme editor */
  class DsBase extends HTMLElement {
    listen(target, type, fn, opts) {
      (this._off ||= []).push(() => target.removeEventListener(type, fn, opts));
      target.addEventListener(type, fn, opts);
    }
    observe(io) { (this._ios ||= []).push(io); return io; }
    disconnectedCallback() {
      (this._off || []).forEach((f) => f());
      (this._ios || []).forEach((io) => io.disconnect());
      this._off = [];
      this._ios = [];
      this._ready = false;
    }
    initWords() {
      const groups = [...this.querySelectorAll('[data-words]')];
      groups.forEach(splitWords);
      if (reduce) { groups.forEach((g) => g.classList.add('words-in')); return; }
      // reversible, like Apple: plays when the block enters, resets once it has fully left the screen
      const out = this.observe(new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting) e.target.classList.remove('words-in'); }), { threshold: 0 }));
      const io = this.observe(new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) e.target.classList.add('words-in'); }), { rootMargin: '-12% 0px -18% 0px' }));
      groups.forEach((g) => {
        if (g.classList.contains('chapter') || g.hasAttribute('data-manual')) return;
        io.observe(g);
        out.observe(g);
      });
    }
    /* reveal on scroll: visible by default, animated only when JS marks it pending */
    initReveal(root = this) {
      if (!this._revealIn) {
        this._revealIn = this.observe(new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.remove('pending'); e.target.classList.add('in-view'); } }), { rootMargin: '-6% 0px -12% 0px' }));
        this._revealOut = this.observe(new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting && !reduce) { e.target.classList.add('pending'); e.target.classList.remove('in-view'); } }), { threshold: 0 }));
      }
      const scroller = (el) => { const s = el.parentElement; return s && s.scrollWidth > s.clientWidth + 4; };
      root.querySelectorAll('.dreveal').forEach((el) => {
        if (el._rv) return;
        el._rv = true;
        if (scroller(el)) { el.classList.add('in-view'); return; }
        if (!reduce && el.getBoundingClientRect().top > innerHeight) el.classList.add('pending');
        else el.classList.add('in-view');
        this._revealIn.observe(el);
        this._revealOut.observe(el);
      });
      root.querySelectorAll('.bento,.rail,.rv-videos').forEach((row) => {
        if (row._rv || reduce || row.scrollWidth <= row.clientWidth + 4) return;
        row._rv = true;
        row.classList.add('dreveal', 'pending');
        this._revealIn.observe(row);
        this._revealOut.observe(row);
      });
    }
  }

  /* ================= <ds-product>: options, stock, product form, gallery ================= */
  class DsProduct extends DsBase {
    connectedCallback() {
      if (this._ready) return;
      const cfgEl = this.querySelector('script[data-ds-config]');
      if (!cfgEl) return;
      this._ready = true;
      this.cfg = JSON.parse(cfgEl.textContent);
      this.en = !!this.cfg.en;
      this.sid = this.cfg.sectionId;
      this.t = this.en
        ? { lastInCart: (s) => `The last one in ${s} is in your cart.`, combo: 'This combination is no longer available.', one: () => 'Only 1 left!', few: (n) => `Only ${n} left!` }
        : { lastInCart: (s) => `La dernière pièce en ${s} est dans ton panier.`, combo: "Cette combinaison n'est plus disponible.", one: () => '1 pièce restante seulement\u202f!', few: (n) => `${n} pièces restantes seulement\u202f!` }; // low stock (Kiana, 10 Oct 2026); \u202f = French thin space before « ! »
      this.cartQty = {};
      this.headerVar();
      this.keepScrollOnColourSwitch();
      this.initVariants();
      this.initGallery();
    }

    headerVar() {
      const set = () => {
        const h = header();
        const pos = h ? getComputedStyle(h).position : '';
        this.style.setProperty('--header-h', (h && (pos === 'sticky' || pos === 'fixed') ? h.offsetHeight : 0) + 'px');
      };
      set();
      this.listen(window, 'resize', set, { passive: true });
    }

    /* colour circles go to the sibling's own page; the shopper keeps their place on the page */
    keepScrollOnColourSwitch() {
      const KEY = 'ds-keep-scroll';
      const apply = () => {
        if (document.documentElement.hasAttribute('data-ds-kept')) return; // already done before the first paint
        try {
          const k = JSON.parse(sessionStorage.getItem(KEY) || 'null');
          sessionStorage.removeItem(KEY);
          if (k && k.to === location.pathname && Date.now() - k.t < 15000) requestAnimationFrame(() => window.scrollTo(0, k.y));
        } catch (e) {}
      };
      if (document.prerendering) document.addEventListener('prerenderingchange', apply, { once: true });
      else apply();
      // once the colour circles are on screen: fetch the other colours' page and main photo, so a tap opens them instantly
      const links = [...this.querySelectorAll('[data-switch]:not(.on)')];
      const row = this.querySelector('.sw');
      if (row && links.length) {
        const io = this.observe(new IntersectionObserver((es) => {
          if (!es[0].isIntersecting) return;
          io.disconnect();
          const go = () => {
            const urls = links.map((a) => a.href);
            if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
              const sr = document.createElement('script');
              sr.type = 'speculationrules';
              sr.textContent = JSON.stringify({ prefetch: [{ urls, eagerness: 'immediate' }] });
              document.head.appendChild(sr);
            } else {
              urls.forEach((u) => { const l = document.createElement('link'); l.rel = 'prefetch'; l.href = u; document.head.appendChild(l); });
            }
            const gal = this.querySelector('.gal .gi img');
            links.forEach((a) => {
              if (!a.dataset.imgSrcset) return;
              const im = new Image();
              im.sizes = (gal && gal.getAttribute('sizes')) || '100vw';
              im.srcset = a.dataset.imgSrcset;
            });
          };
          (window.requestIdleCallback || ((f) => setTimeout(f, 600)))(go);
        }, { rootMargin: '200px 0px' }));
        io.observe(row);
      }
      this.querySelectorAll('[data-switch]').forEach((a) => {
        const prefetch = () => {
          if (a._pf || (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) return;
          a._pf = true;
          const l = document.createElement('link');
          l.rel = 'prefetch';
          l.href = a.href;
          document.head.appendChild(l);
        };
        a.addEventListener('mouseenter', prefetch, { passive: true });
        a.addEventListener('touchstart', prefetch, { passive: true });
        a.addEventListener('click', (e) => {
          if (a.classList.contains('on')) { e.preventDefault(); return; }
          try { sessionStorage.setItem(KEY, JSON.stringify({ y: scrollY, to: new URL(a.href).pathname, t: Date.now() })); } catch (err) {}
        });
      });
    }

    initVariants() {
      this.variants = this.cfg.variants || [];
      this.vs = this.querySelector(`#variant-selects-${this.sid}`);
      this.idInput = this.querySelector('product-form input[name="id"]');
      this.btn = document.getElementById(`ProductSubmitButton-${this.sid}`);
      this.nOpts = this.variants[0] ? this.variants[0].options.length : 0;
      if (this.vs) {
        this.vs.addEventListener('click', (e) => {
          const l = e.target.closest('label');
          if (l && l.classList.contains('out')) e.preventDefault();
        });
        this.vs.addEventListener('change', (e) => {
          if (e.target.classList.contains('ds-radio') || e.target.classList.contains('ds-select')) this.onOptionChange();
        });
      }
      this.initDropdowns();
      this.sync(false);
      this.loadCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        const u1 = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.loadCart());
        const u2 = subscribe(PUB_SUB_EVENTS.cartError, () => this.loadCart());
        (this._off ||= []).push(u1, u2);
      }
    }

    /* flag: a dropdown drawn by the page (black, same style as the pills), driving the real <select> underneath.
       The browser's own menu would open white on some phones and computers. */
    initDropdowns() {
      this.querySelectorAll('[data-ds-dd]').forEach((box) => {
        const sel = box.querySelector('select.ds-select');
        if (!sel || box._dd) return;
        box._dd = true;
        box.classList.add('enhanced');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dd-btn';
        btn.setAttribute('aria-haspopup', 'listbox');
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-label', sel.getAttribute('aria-label') || '');
        const val = document.createElement('span');
        val.className = 'dd-val';
        btn.appendChild(val);
        const list = document.createElement('ul');
        list.className = 'dd-list';
        list.setAttribute('role', 'listbox');
        list.hidden = true;
        box.append(btn, list);
        const label = () => { const o = sel.options[sel.selectedIndex]; val.textContent = o ? o.dataset.label || o.value : ''; };
        const build = () => {
          list.innerHTML = '';
          [...sel.options].forEach((o, i) => {
            const li = document.createElement('li');
            li.setAttribute('role', 'option');
            li.tabIndex = -1;
            li.dataset.i = i;
            li.textContent = o.textContent;
            li.setAttribute('aria-selected', i === sel.selectedIndex);
            if (o.textContent !== (o.dataset.label || o.value)) li.classList.add('out');
            list.appendChild(li);
          });
        };
        const open = () => {
          build();
          list.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
          const cur = list.children[sel.selectedIndex] || list.firstElementChild;
          if (cur) cur.focus({ preventScroll: true });
        };
        const close = (focus) => {
          if (list.hidden) return;
          list.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
          if (focus) btn.focus({ preventScroll: true });
        };
        // focus goes back to the button only after a keyboard choice: after a tap, Safari would draw the white
        // keyboard focus ring around the button = a white rectangle (Kiana, 11 Oct 2026)
        const pick = (i, kb) => {
          if (sel.selectedIndex !== i) { sel.selectedIndex = i; sel.dispatchEvent(new Event('change', { bubbles: true })); }
          label();
          close(kb);
        };
        btn.addEventListener('click', () => (list.hidden ? open() : close()));
        btn.addEventListener('keydown', (e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); } });
        list.addEventListener('click', (e) => { const li = e.target.closest('li'); if (li) pick(+li.dataset.i, e.detail === 0); });
        list.addEventListener('keydown', (e) => {
          const items = [...list.children];
          const i = items.indexOf(document.activeElement);
          if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[i] || items[0]).focus({ preventScroll: true }); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[i] || items[0]).focus({ preventScroll: true }); }
          else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (i >= 0) pick(i, true); }
          else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
          else if (e.key === 'Tab') close();
        });
        this.listen(document, 'click', (e) => { if (!box.contains(e.target)) close(); });
        sel.addEventListener('change', label);
        label();
      });
    }

    selected() {
      const out = [];
      for (let p = 1; p <= this.nOpts; p++) {
        const step = this.vs && this.vs.querySelector(`[data-ds-option="${p}"]`);
        const sel = step && step.querySelector('select.ds-select');
        const r = step && step.querySelector('.ds-radio:checked');
        out.push(sel ? sel.value : r ? r.value : this.variants[0].options[p - 1]);
      }
      return out;
    }
    find(opts) {
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    }
    remaining(v) {
      if (!v || !v.available) return 0;
      if (v.mgmt !== 'shopify' || v.policy === 'continue') return Infinity;
      return v.inv - (this.cartQty[v.id] || 0);
    }

    async loadCart() {
      try {
        const res = await fetch(this.cfg.cartUrl, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.cartQty = {};
        (cart.items || []).forEach((it) => { this.cartQty[it.variant_id] = (this.cartQty[it.variant_id] || 0) + it.quantity; });
        this.sync(false);
      } catch (e) {}
    }

    onOptionChange() {
      const v = this.sync(true);
      if (!v) return;
      try {
        const u = new URL(location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', u.toString());
      } catch (e) {}
      if (this.idInput) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sid, html: null, variant: v } });
      }
    }

    sync(changed) {
      const opts = this.selected();
      const v = this.find(opts);
      const left = this.remaining(v);
      const ok = !!v && left > 0;
      const vstr = window.variantStrings || {};

      // pills: a value is "out" when, with the other choices kept, nothing is left to buy
      if (this.vs) {
        this.vs.querySelectorAll('[data-ds-option]').forEach((step) => {
          const p = +step.dataset.dsOption;
          step.querySelectorAll('.ds-radio').forEach((r) => {
            const test = opts.slice();
            test[p - 1] = r.value;
            const out = this.remaining(this.find(test)) <= 0;
            const label = r.nextElementSibling;
            if (label) label.classList.toggle('out', out);
            r.setAttribute('aria-disabled', out);
          });
          // flag dropdown: « épuisé » after a flag that isn't available with the other choices (still selectable)
          step.querySelectorAll('select.ds-select option').forEach((o) => {
            const test = opts.slice();
            test[p - 1] = o.value;
            const out = this.remaining(this.find(test)) <= 0;
            const txt = (o.dataset.label || o.value) + (out ? (this.en ? ' — sold out' : ' — épuisé') : '');
            if (o.textContent !== txt) o.textContent = txt;
          });
        });
      }

      // main button (the sticky bar mirrors its disabled state and text)
      if (this.btn) {
        this.btn.toggleAttribute('disabled', !ok);
        const span = this.btn.querySelector('span');
        const txt = !v ? vstr.unavailable : !ok ? vstr.soldOut : vstr.addToCart;
        if (span && txt && span.textContent.trim() !== txt) span.textContent = txt;
      }
      if (this.idInput && v) {
        this.idInput.value = v.id;
        this.idInput.disabled = false;
      }

      // price + shipping line (only when the variant really changes)
      if (v && changed) {
        const pe = this.querySelector('[data-ds-price]');
        if (pe) pe.textContent = v.price;
        let was = this.querySelector('[data-ds-was]');
        if (v.compare) {
          if (!was && pe) { was = document.createElement('s'); was.className = 'was'; was.dataset.dsWas = ''; pe.before(was); }
          was.textContent = v.compare;
        } else if (was) was.remove();
      }

      // stock line under the sizes
      const note = this.querySelector('[data-ds-stock]');
      if (note) {
        const size = this.cfg.sizePos ? opts[this.cfg.sizePos - 1] : '';
        const inCart = v ? this.cartQty[v.id] || 0 : 0;
        let msg = '';
        if (v && v.available && left <= 0 && inCart) msg = this.t.lastInCart(size);
        else if ((!v || left <= 0) && this.cfg.flagPos) msg = this.t.combo;
        else if (left !== Infinity && left > 0 && left <= 2) msg = left === 1 ? this.t.one(size) : this.t.few(left, size);
        if (note.textContent !== msg) note.textContent = msg;
      }

      // flag close-up beside the choices (Allié·e = the sleeve with no flag)
      if (this.cfg.flagPos) {
        const fv = (opts[this.cfg.flagPos - 1] || '').toUpperCase();
        const key = /ALLI|ALLY/.test(fv) ? 'ALLIE' : /LESB/.test(fv) ? 'LESBIENNE' : /GAY|GAI/.test(fv) ? 'GAY' : /TRANS/.test(fv) ? 'TRANS' : /PAN/.test(fv) ? 'PANSEXUEL' : /\bBI/.test(fv) ? 'BISEXUEL' : 'LGBT';
        const img = this.querySelector('[data-ds-flag-img]');
        const src = (this.cfg.flagImgs || {})[key];
        if (img && src && img.dataset.key !== key) {
          const first = !img.dataset.key;
          img.dataset.key = key;
          if (!first || changed) {
            img.src = src;
            img.classList.remove('swap');
            void img.offsetWidth;
            img.classList.add('swap');
          }
        }
        const fn = this.querySelector('[data-ds-flag-note]');
        if (fn) fn.hidden = key !== 'ALLIE';
        const fc = this.querySelector('[data-ds-flag-cap]');
        if (fc) fc.hidden = key === 'ALLIE';
      }
      return v;
    }

    initGallery() {
      const gal = this.querySelector('.gal');
      if (!gal) return;
      const dots = this.querySelectorAll('.gal-count i');
      if (dots.length) {
        const count = this.querySelector('.gal-count');
        let raf = 0;
        gal.addEventListener('scroll', () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = 0;
            const first = gal.firstElementChild;
            if (!first) return;
            const step = first.offsetWidth + 12;
            const i = Math.round(gal.scrollLeft / step);
            const d = ((i % 3) + 3) % 3; // 3 dots that cycle, one step per photo
            dots.forEach((el, k) => el.classList.toggle('on', k === d));
            // phone: the dots pill sits on the photo; the last photo stops further right (no photo peeks after it),
            // so the pill slides right with it between the second-last and the last photo
            const max = gal.scrollWidth - gal.clientWidth;
            const from = (gal.children.length - 2) * step;
            const t = max > from ? Math.min(1, Math.max(0, (gal.scrollLeft - from) / (max - from))) : 0;
            const peek = Math.max(0, gal.clientWidth - parseFloat(getComputedStyle(gal).paddingLeft) * 2 - first.offsetWidth);
            count.style.setProperty('--ds-shift', `${(t * peek).toFixed(1)}px`);
          });
        }, { passive: true });
      }
      const lb = this.querySelector(`#DsLightbox-${this.sid}`);
      gal.addEventListener('click', (e) => {
        const b = e.target.closest('[data-lb]');
        if (!b || !lb) return;
        const img = lb.querySelector('[data-lb-img]');
        img.src = b.dataset.lb;
        img.alt = (b.querySelector('img') || {}).alt || '';
        openModal(lb, b);
      });
    }
  }

  /* ================= <ds-why>: « Chacun·e a sa raison » + the quote on black ================= */
  class DsWhy extends DsBase {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.initWords();
      this.fitSub();
      this.listen(window, 'resize', () => requestAnimationFrame(() => this.fitSub()), { passive: true });
      this.initReasons();
      this.initTom();
    }
    /* « Un regard. Une discussion. Une rencontre. »: one line when it fits, else one phrase per line */
    fitSub() {
      this.querySelectorAll('.why-head .sub').forEach((sub) => {
        sub.classList.remove('stack');
        const ph = [...sub.querySelectorAll('.ph')];
        if (ph.some((x) => x.offsetTop !== ph[0].offsetTop)) sub.classList.add('stack');
      });
    }
    initReasons() {
      const head = this.querySelector('.why-head');
      const why = this.querySelector('.why');
      const cards = [...this.querySelectorAll('.reason')];
      const rg = this.querySelector('.reasons');
      if (rg) {
        const btns = this.querySelectorAll('[data-rn]');
        const step = () => { const c = rg.querySelector('.reason'); return c ? c.offsetWidth + 20 : 400; };
        const sync = () => {
          if (btns.length < 2) return;
          btns[0].disabled = rg.scrollLeft < 4;
          btns[1].disabled = rg.scrollLeft > rg.scrollWidth - rg.clientWidth - 4;
        };
        btns.forEach((b) => b.addEventListener('click', () => rg.scrollBy({ left: +b.dataset.rn * step(), behavior: reduce ? 'auto' : 'smooth' })));
        rg.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
        sync();
      }
      if (!head || !why) return;
      if (reduce || (window.Shopify && window.Shopify.designMode)) { cards.forEach((c) => c.classList.add('shown')); return; }
      // photos appear after the heading has played (≈ 0.9 s), one after the other; scrolling on finishes everything at once
      const WHY_TEXT_MS = 900;
      let readyAt = null;
      let startY = 0;
      this.listen(window, 'scroll', () => {
        if (readyAt === null || why.classList.contains('rush') || Math.abs(scrollY - startY) < 60) return;
        if (performance.now() > readyAt + 900) return;
        why.classList.add('rush');
        readyAt = performance.now();
        cards.forEach((c) => {
          const r = c.getBoundingClientRect();
          if (r.top < innerHeight && r.bottom > 0 && r.left < innerWidth && r.right > 0) { c.style.transitionDelay = '0ms'; c.classList.add('shown'); }
        });
      }, { passive: true });
      const io = this.observe(new IntersectionObserver((es) => es.forEach((e) => {
        if (e.target === head) {
          if (e.isIntersecting && readyAt === null) { readyAt = performance.now() + WHY_TEXT_MS; startY = scrollY; }
          if (!e.isIntersecting && e.boundingClientRect.top > 0) { readyAt = null; why.classList.remove('rush'); }
          return;
        }
        const c = e.target;
        if (e.isIntersecting) {
          const wait = why.classList.contains('rush') ? 0 : Math.max(0, (readyAt ?? performance.now() + WHY_TEXT_MS) - performance.now());
          c.style.transitionDelay = Math.round(wait + (+c.dataset.n % 3) * 260) + 'ms';
          c.classList.add('shown');
        } else { c.style.transitionDelay = '0ms'; c.classList.remove('shown'); }
      }), { rootMargin: '0px 0px -10% 0px' }));
      io.observe(head);
      cards.forEach((c) => io.observe(c));
    }
    initTom() {
      const tom = this.querySelector('[data-ds-tom]');
      if (!tom) return;
      const stage = tom.querySelector('.tom-stage');
      const copyEl = stage.querySelector('.tom-copy');
      const lastEl = () => { const nav = this.querySelector('.reasons-nav'); return nav && nav.offsetParent ? nav : this.querySelector('.reasons'); };
      // the black stage is pinned exactly when the last photo goes under the header (+ the sticky bar on desktop)
      const placeStage = () => {
        const hdr = header();
        const bar = stickyBar();
        const rs = lastEl();
        if (!rs) return;
        const chrome = (hdr ? hdr.offsetHeight : 0) + (innerWidth >= 750 && bar ? bar.offsetHeight : 0);
        const cur = parseFloat(getComputedStyle(tom).marginTop) || 0;
        const gap = tom.getBoundingClientRect().top - rs.getBoundingClientRect().bottom;
        tom.style.marginTop = cur + (-chrome - gap) + 'px';
      };
      placeStage();
      this.listen(window, 'resize', placeStage, { passive: true });
      this.listen(window, 'load', placeStage, { once: true });
      // headline + body + reviewer line + closing line + button: one left-to-right wave
      splitWords(copyEl);
      const cap = copyEl.querySelector('figcaption');
      const quoteOnly = copyEl.querySelectorAll('blockquote .w').length;
      if (cap && !cap.dataset.split) {
        cap.dataset.split = '1';
        let i = 0;
        const frag = document.createDocumentFragment();
        cap.textContent.split(/([ \t\n\r]+)/).forEach((part) => {
          if (!part) return;
          if (/^[ \t\n\r]+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          const sp = document.createElement('span');
          sp.className = 'w';
          sp.style.setProperty('--i', i++);
          sp.textContent = part;
          frag.appendChild(sp);
        });
        cap.textContent = '';
        cap.appendChild(frag);
        cap.style.setProperty('--d', quoteOnly * 90 + 150 + 'ms');
      }
      const endB = stage.querySelector('.tom-end b');
      const quoteWords = copyEl.querySelectorAll('.w').length;
      splitWords(endB);
      endB.style.setProperty('--d', quoteWords * 90 + 300 + 'ms');
      const paintGrad = () => {
        const r = endB.getBoundingClientRect();
        endB.querySelectorAll('.w').forEach((w) => {
          w.style.backgroundImage = 'var(--grad-text)';
          w.style.backgroundSize = r.width + 'px 100%';
          w.style.backgroundPosition = -(w.getBoundingClientRect().left - r.left) + 'px 0';
        });
      };
      paintGrad();
      this.listen(window, 'resize', paintGrad, { passive: true });
      const endWords = endB.querySelectorAll('.w').length;
      const pill = stage.querySelector('.tom-end .dpill');
      if (pill) pill.style.setProperty('--s', (quoteWords + endWords) * 90 + 300 + 700 + 'ms');
      if (reduce) { stage.classList.add('on'); return; }
      // like Apple: the copy plays once everything on screen is black, i.e. the last photos have gone under the
      // header (+ the sticky bar on desktop). An IntersectionObserver does it with no work while scrolling.
      let watcher = null;
      const watch = () => {
        const le = lastEl();
        if (!le) return;
        if (watcher) watcher.disconnect();
        const chrome = Math.round((header() ? header().offsetHeight : 0) + (innerWidth >= 750 && stickyBar() ? stickyBar().offsetHeight : 0));
        watcher = this.observe(new IntersectionObserver((es) => es.forEach((e) => {
          if (e.isIntersecting) stage.classList.remove('on');
          else if (e.boundingClientRect.top < innerHeight / 2) stage.classList.add('on');
        }), { rootMargin: `-${chrome + 2}px 0px 0px 0px`, threshold: 0 }));
        watcher.observe(le);
      };
      watch();
      this.listen(window, 'resize', watch, { passive: true });
    }
  }

  /* ================= <ds-story>: one photo, blurred while the text is on it, then sharp ================= */
  class DsStory extends DsBase {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const band = this.querySelector('.story');
      const mid = this.querySelector('.story-mid');
      if (!band || !mid) return;
      // nothing runs while scrolling: the observers only fire when a line is crossed, then the CSS fades do the rest
      const set = (k, on) => band.classList.toggle(k, on);
      let root = null; // the page itself (also inside the theme editor's frame, where the screen-wide default would ignore the margins)
      try { new IntersectionObserver(() => {}, { root: document }); root = document; } catch (e) { root = null; }
      const io = (cb, rootMargin, el) => this.observe(new IntersectionObserver(([e]) => cb(e), { root, rootMargin })).observe(el);
      // the text rises in once the top of the band is 20 % into the screen; reset once the band is off screen below
      io((e) => { if (e.isIntersecting) set('text-in', true); }, '0px 0px -20% 0px', band);
      io((e) => { if (!e.isIntersecting && e.boundingClientRect.top > 0) { set('text-in', false); set('clear', false); } }, '0px', band);
      // the photo turns sharp once the middle of the band passes the middle of the screen,
      // and blurs again only once it's back below 62 % (a margin, so it never flickers at the line)
      io((e) => { if (e.isIntersecting || e.boundingClientRect.bottom < 0) set('clear', true); }, '0px 0px -50% 0px', mid);
      io((e) => { if (!e.isIntersecting && e.boundingClientRect.top > innerHeight * 0.62) set('clear', false); }, '0px 0px -38% 0px', mid);
    }
  }

  /* ================= <ds-quality>: one detail at a time, cycling every 3.6 s until someone taps a pill ================= */
  class DsQuality extends DsBase {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.initWords();
      this.initReveal();
      const qd = this.querySelector('.q-detail');
      const chips = [...this.querySelectorAll('.q-chips button')];
      if (!qd || !chips.length) return;
      let qi = 0;
      let timer = null;
      let stopped = false;
      const show = (i) => {
        qi = i;
        chips.forEach((c, n) => c.setAttribute('aria-pressed', n === i));
        qd.innerHTML = `<b>${esc(chips[i].dataset.qTitle)}</b><p>${esc(chips[i].dataset.qText)}</p>`;
        qd.classList.remove('swap');
        void qd.offsetWidth;
        qd.classList.add('swap');
      };
      chips.forEach((c, n) => c.addEventListener('click', () => { stopped = true; clearInterval(timer); show(n); }));
      (this._off ||= []).push(() => clearInterval(timer));
      if (reduce) return;
      this.observe(new IntersectionObserver((es) => {
        clearInterval(timer);
        if (es[0].isIntersecting && !stopped) timer = setInterval(() => { if (!document.hidden) show((qi + 1) % chips.length); }, 3600);
      })).observe(qd);
    }
  }

  /* ================= <ds-reviews>: Judge.me, as written ================= */
  class DsReviews extends DsBase {
    connectedCallback() {
      if (this._ready) return;
      const cfgEl = this.querySelector('script[data-ds-reviews-config]');
      if (!cfgEl) return;
      this._ready = true;
      this.cfg = JSON.parse(cfgEl.textContent);
      this.en = !!this.cfg.en;
      this.t = this.en
        ? { verified: 'Verified buyer', otherLang: ' · in French', translated: ' · translated from French', prev: 'Previous review', next: 'Next review', reviews: 'Customer reviews' }
        : { verified: 'Achat vérifié', otherLang: ' · en anglais', translated: " · traduit de l'anglais", prev: 'Avis précédent', next: 'Avis suivant', reviews: 'Avis clients' };
      this.initWords();
      this.initReveal();
      this.initVideos();
      const io = this.observe(new IntersectionObserver((es) => {
        if (!es[0].isIntersecting) return;
        io.disconnect();
        this.loadReviews();
      }, { rootMargin: '1200px 0px' }));
      io.observe(this);
    }

    initVideos() {
      this.querySelectorAll('.rv-v').forEach((box) => {
        const v = box.querySelector('video');
        const b = box.querySelector('.rv-play');
        if (!v || !b) return;
        b.addEventListener('click', () => {
          this.querySelectorAll('.rv-v video').forEach((o) => { if (o !== v) o.pause(); });
          if (!v.getAttribute('src')) v.src = v.dataset.src;
          v.controls = true;
          v.play();
          box.classList.add('playing');
        });
        v.addEventListener('pause', () => { box.classList.remove('playing'); v.controls = false; });
      });
    }

    async loadReviews() {
      this.querySelectorAll('video[data-poster]').forEach((v) => { v.poster = v.dataset.poster; });
      const feat = this.querySelector('[data-rv-feature]');
      let items = [];
      try {
        const res = await fetch(this.cfg.reviewsUrl);
        items = this.parse(await res.text());
      } catch (e) {}
      this.render(items);
      if (feat) feat.removeAttribute('aria-busy');
    }

    parse(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      try { this.tr = JSON.parse((doc.querySelector('script[data-ds-tr]') || {}).textContent || '{}'); } catch (e) { this.tr = {}; }
      const months = MONTHS[this.en ? 'en' : 'fr'];
      const out = [];
      const seen = new Set();
      doc.querySelectorAll('template[data-h]').forEach((tpl) => {
        tpl.content.querySelectorAll('.jdgm-rev').forEach((r) => {
          const rating = r.querySelector('.jdgm-rev__rating');
          const stamp = r.querySelector('.jdgm-rev__timestamp');
          const stars = +((rating && rating.dataset.score) || 0);
          const ts = (stamp && stamp.dataset.content) || '';
          const raw = ((r.querySelector('.jdgm-rev__author') || {}).textContent || '').replace(/\s+/g, ' ').trim();
          const title = ((r.querySelector('.jdgm-rev__title') || {}).textContent || '').replace(/\s+/g, ' ').trim();
          const body = r.querySelector('.jdgm-rev__body');
          if (!body) return;
          body.querySelectorAll('br').forEach((b) => b.replaceWith(' '));
          const ps = [...body.querySelectorAll('p')].map((p) => p.textContent.trim()).filter(Boolean);
          const text = (ps.length ? ps.join(' ') : body.textContent).replace(/\s+/g, ' ').trim();
          if (!text) return;
          // names as first name + initial(s), like the rest of the site (« Alain Faubert » → « Alain F. », « Billie Dxxx-Pxxx » → « Billie D.-P. »)
          const words = raw.split(' ');
          const last = words[words.length - 1] || '';
          const name = words.length > 1 && last.replace(/[^\p{L}]/gu, '').length > 2 && !last.includes('.')
            ? `${words.slice(0, -1).join(' ')} ${last.split('-').filter(Boolean).map((p) => p[0].toUpperCase() + '.').join('-')}`
            : raw;
          const m = ts.match(/^(\d{4})-(\d{2})/);
          const y = m ? +m[1] : 0;
          const mo = m ? +m[2] - 1 : 0;
          const key = raw + '|' + text;
          if (seen.has(key)) return;
          seen.add(key);
          out.push({ id8: (r.dataset.reviewId || '').slice(0, 8), h: tpl.dataset.h, ptype: tpl.dataset.type, stars, title, text, name, verified: r.dataset.verifiedBuyer === 'true', time: y * 12 + mo, date: m ? `${months[mo]} ${y}` : '' });
        });
      });
      return out.sort((a, b) => b.time - a.time);
    }

    render(items) {
      const c = this.cfg;
      const all = items.filter((r) => r.stars >= 4); // reviews under 4 stars are not displayed on the page
      const famSet = new Set([c.handle, ...(c.family || [])]);
      const group = (t) => (t === 'allie' || t === 'chood' ? 'hoodie' : t);
      const general = c.type === 'gift' || c.type === 'other';
      const sameProduct = general ? [] : all.filter((r) => famSet.has(r.h) || (c.type === 'socks' && r.ptype === 'socks'));
      const sameType = general ? [] : all.filter((r) => !sameProduct.includes(r) && group(r.ptype) === c.typeGroup);
      // other products' reviews only fill in when they don't name another product (a cap page never shows « coton ouate »)
      const OTHER = /hoodie|coton ouate|chandail|crewneck|casquette|chaussette|\bbas\b|t-?shirt|\bshirt\b|\bpins?\b|capuchon|\bhood\b|manche|zip|sleeve/i;
      const CLOTHING = /medium|large|small|\bxl\b|taille|tissu|molleton|coupe|lavage|rapetiss|épais|chaud|porte du/i;
      const accessory = ['cap', 'pin', 'socks'].includes(c.type);
      const rest = general
        ? all
        : all.filter((r) => {
            const t = r.text + ' ' + (r.title || '');
            return !sameProduct.includes(r) && !sameType.includes(r) && !OTHER.test(t) && !(accessory && CLOTHING.test(t)) && !(c.type === 'pin' && /confort/i.test(r.text));
          });
      const own = [...sameProduct, ...sameType];
      const key = (r) => r.name + '|' + r.text;
      const pick = [];
      for (const r of [...own, ...(own.length < 4 ? rest : [])]) if (!pick.some((x) => key(x) === key(r))) pick.push(r);
      // rotating quote: the best first (5★, verified, a real sentence about quality, comfort or meaning, this product, French, recent), top 12
      const famKeys = new Set(sameProduct.map(key));
      const typeKeys = new Set(sameType.map(key));
      const d = new Date();
      const now = d.getFullYear() * 12 + d.getMonth();
      const WORDS = /qualit|confort|confo|doux|douce|chaud|épais|lavage|brod|communaut|fier|safe|message|visibilit|allié|adore|parfait|quality|comfy|soft/gi;
      const score = (r) => {
        const L = r.text.length;
        const kw = Math.min(3, (r.text.match(WORDS) || []).length);
        return r.stars * 10 + (r.verified ? 3 : 0) + (L < 30 ? -15 : L < 60 ? 2 : L <= 140 ? 8 : 5) + kw * 3 + (famKeys.has(key(r)) ? 40 : typeKeys.has(key(r)) ? 20 : 0) + (isEN(r.text) ? 0 : 2) - Math.max(0, now - r.time) * 0.2;
      };
      const best = [...pick].sort((a, b) => score(b) - score(a)).slice(0, 12);
      // shown in the page's language: a review in the other language uses its faithful translation (labelled), never edited
      const want = this.en ? 'en' : 'fr';
      const view = (r) => {
        const orig = isEN(r.text) ? 'en' : 'fr';
        if (orig === want) return { text: r.text, title: r.title, lang: orig, note: '' };
        const t = (this.tr || {})[r.id8];
        if (t && t.lang === want) return { text: t.tx, title: t.ti, lang: want, note: this.t.translated };
        return { text: r.text, title: r.title, lang: orig, note: this.t.otherLang };
      };
      const feat = this.querySelector('[data-rv-feature]');
      if (feat) {
        if (!best.length) feat.hidden = true;
        else {
          feat.innerHTML =
            `<div class="qstack">${best.map((r, i) => { const v = view(r); return `<figure class="dquote ${i === 0 ? 'on' : ''}" aria-hidden="${i !== 0}"><blockquote lang="${v.lang}" class="${v.text.length > 200 ? 'q-xl' : v.text.length > 120 ? 'q-l' : ''}">${esc(v.text)}</blockquote><figcaption class="by">${starHTML(r.stars)} <span>${esc(r.name)}</span><span class="dbadge">${r.verified ? '✓ ' + this.t.verified + ' · ' : ''}${esc(r.date)}${v.note}</span></figcaption></figure>`; }).join('')}</div>` +
            (best.length > 1 ? `<div class="feat-ctrl"><button type="button" data-q="-1" aria-label="${this.t.prev}">‹</button><button type="button" data-q="1" aria-label="${this.t.next}">›</button><div class="feat-bar"><b></b></div></div>` : '');
          this.wireQuotes(feat);
        }
      }
      const railBox = this.querySelector('[data-rv-rail]');
      if (railBox && own.length > 1) {
        railBox.innerHTML = `<div class="rail rv-rail" aria-label="${this.t.reviews}">${own.map((r) => { const v = view(r); return `<article class="rcard dreveal">${starHTML(r.stars)}${v.title ? `<h3>${esc(v.title)}</h3>` : '<h3 aria-hidden="true">&nbsp;</h3>'}<p lang="${v.lang}">${esc(v.text)}</p><div class="who"><b>${esc(r.name)}</b><span>${r.verified ? this.t.verified + ' · ' : ''}${esc(r.date)}${v.note}</span></div></article>`; }).join('')}</div>`;
        this.initReveal(railBox);
      }
    }

    wireQuotes(feat) {
      const qs = [...feat.querySelectorAll('.dquote')];
      const stack = feat.querySelector('.qstack');
      const bar = feat.querySelector('.feat-bar b');
      let qi = 0;
      let timer = null;
      let paused = false;
      let inView = false;
      const fitQ = () => { if (stack && qs[qi]) stack.style.height = qs[qi].offsetHeight + 'px'; }; // the box follows the review on screen
      fitQ();
      this.listen(window, 'resize', fitQ, { passive: true });
      const restartBar = () => {
        if (!bar) return;
        bar.style.transition = 'none';
        bar.style.width = '0';
        void bar.offsetWidth;
        if (!reduce && inView && !paused) { bar.style.transition = 'width 7000ms linear'; bar.style.width = '100%'; }
      };
      const show = (n) => {
        qi = (n + qs.length) % qs.length;
        qs.forEach((q, i) => { q.classList.toggle('on', i === qi); q.setAttribute('aria-hidden', i !== qi); });
        fitQ();
        restartBar();
      };
      if (qs.length < 2) return;
      feat.querySelectorAll('[data-q]').forEach((b) => b.addEventListener('click', () => { show(qi + +b.dataset.q); }));
      const start = () => { clearInterval(timer); if (!reduce) timer = setInterval(() => { if (!paused && inView && !document.hidden) show(qi + 1); }, 7000); restartBar(); };
      (this._off ||= []).push(() => clearInterval(timer));
      feat.addEventListener('mouseenter', () => { paused = true; if (bar) { const w = getComputedStyle(bar).width; bar.style.transition = 'none'; bar.style.width = w; } });
      feat.addEventListener('mouseleave', () => { paused = false; start(); });
      feat.addEventListener('focusin', () => { paused = true; });
      feat.addEventListener('focusout', () => { paused = false; start(); });
      this.observe(new IntersectionObserver((es) => { inView = es[0].isIntersecting; if (inView) start(); else clearInterval(timer); })).observe(feat);
    }
  }

  customElements.define('ds-product', DsProduct);
  customElements.define('ds-why', DsWhy);
  customElements.define('ds-story', DsStory);
  customElements.define('ds-quality', DsQuality);
  customElements.define('ds-reviews', DsReviews);
})();
